import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";

const Money = ({ v }) => <span className="font-serif text-2xl text-[#F7ECEB]">${Number(v || 0).toFixed(2)}</span>;

export const AdminFees = () => {
  const [fees, setFees] = useState(null);
  const [form, setForm] = useState({ customer_fee_pct: "", provider_commission_pct: "" });
  const [revenue, setRevenue] = useState(null);
  const [msg, setMsg] = useState("");

  const load = async () => {
    const [f, r] = await Promise.all([api.get("/admin/settings/fees"), api.get("/admin/revenue")]);
    setFees(f.data); setRevenue(r.data);
    setForm({ customer_fee_pct: f.data.customer_fee_pct, provider_commission_pct: f.data.provider_commission_pct });
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    setMsg("");
    try {
      await api.put("/admin/settings/fees", {
        customer_fee_pct: Number(form.customer_fee_pct), provider_commission_pct: Number(form.provider_commission_pct),
      });
      setMsg("Fees updated. New bookings use the new percentages; existing bookings keep theirs.");
      load();
    } catch (e) { setMsg(formatApiError(e.response?.data?.detail)); }
  };

  const base = 100;
  const cf = Number(form.customer_fee_pct || 0), pc = Number(form.provider_commission_pct || 0);

  return (
    <div className="mt-8 grid lg:grid-cols-2 gap-6" data-testid="admin-fees">
      <div className="tt-card p-8">
        <div className="text-xs uppercase tracking-[0.2em] text-[#C93B3B]">Platform fees</div>
        <h2 className="font-serif text-3xl mt-2 text-[#F7ECEB]">Revenue split</h2>
        <p className="text-sm text-[#A89896] mt-2">Both percentages apply to the provider's listed session price. Founder sessions are 100% platform revenue.</p>
        <div className="grid sm:grid-cols-2 gap-4 mt-6">
          <div>
            <label className="text-xs uppercase tracking-[0.2em] text-[#C93B3B]">Customer service fee %</label>
            <input type="number" min={0} max={50} step={0.5} value={form.customer_fee_pct} data-testid="fee-customer-input"
              onChange={(e) => setForm({ ...form, customer_fee_pct: e.target.value })}
              className="w-full mt-2 px-3 py-2 rounded-lg border border-[#382A2A] bg-[#110D0D] text-[#F7ECEB]" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-[0.2em] text-[#C93B3B]">Provider commission %</label>
            <input type="number" min={0} max={50} step={0.5} value={form.provider_commission_pct} data-testid="fee-provider-input"
              onChange={(e) => setForm({ ...form, provider_commission_pct: e.target.value })}
              className="w-full mt-2 px-3 py-2 rounded-lg border border-[#382A2A] bg-[#110D0D] text-[#F7ECEB]" />
          </div>
        </div>
        <div className="mt-6 p-4 rounded-xl bg-[#110D0D] text-sm text-[#A89896] space-y-1" data-testid="fee-preview">
          <div className="text-xs uppercase tracking-[0.2em] text-[#E58B5E] mb-2">Preview · $100 listed session</div>
          <div>Customer pays: <span className="text-[#F7ECEB]">${(base + base * cf / 100).toFixed(2)}</span></div>
          <div>Provider receives: <span className="text-[#F7ECEB]">${(base - base * pc / 100).toFixed(2)}</span></div>
          <div>Touche.Today gross revenue: <span className="text-[#F7ECEB]">${(base * cf / 100 + base * pc / 100).toFixed(2)}</span> (before Stripe fees)</div>
        </div>
        <button onClick={save} className="btn-primary mt-6" data-testid="fee-save">Save fees</button>
        {msg && <div className="text-sm text-[#E58B5E] mt-3" data-testid="fee-msg">{msg}</div>}
        {fees?.updated_at && <div className="text-xs text-[#A89896] mt-3">Last changed {fees.updated_at.slice(0, 10)} by {fees.updated_by}</div>}
      </div>

      <div className="tt-card p-8" data-testid="admin-revenue">
        <div className="text-xs uppercase tracking-[0.2em] text-[#C93B3B]">Ledger</div>
        <h2 className="font-serif text-3xl mt-2 text-[#F7ECEB]">Revenue to date</h2>
        {revenue && (
          <div className="mt-6 grid grid-cols-2 gap-5 text-sm text-[#A89896]">
            <div>Paid sessions<br /><span className="font-serif text-2xl text-[#F7ECEB]">{revenue.sessions}</span></div>
            <div>Paying members<br /><span className="font-serif text-2xl text-[#F7ECEB]">{revenue.paying_members}</span></div>
            <div>Listed prices (base)<br /><Money v={revenue.base_price} /></div>
            <div>Customer fees<br /><Money v={revenue.customer_fee} /></div>
            <div>Customers charged<br /><Money v={revenue.customer_total} /></div>
            <div>Provider commission<br /><Money v={revenue.provider_commission} /></div>
            <div>Owed to providers (net)<br /><Money v={revenue.provider_net} /></div>
            <div>Paid out so far<br /><Money v={revenue.paid_out} /></div>
            <div className="col-span-2 border-t border-[#382A2A] pt-4">Touche.Today gross platform revenue<br /><Money v={revenue.platform_revenue} /></div>
          </div>
        )}
        <p className="text-xs text-[#A89896] mt-5">{revenue?.note}</p>
      </div>
    </div>
  );
};
